/**
 * BoardPanel — Clean-room whiteboard replacement.
 * Layout matches the tldraw whiteboard: sidebar stays visible,
 * topbar with back + title + insert/checklists, bottom floating toolbar.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useBoard } from './useBoard'
import { BoardToolbar } from './BoardToolbar'
import { BoardCanvas } from './BoardCanvas'
import { BoardBottomBar } from './BoardBottomBar'
import type { Tool, ShapeSubType } from './BoardBottomBar'

interface Props {
  projectId: string
  onClose: () => void
}

export function BoardPanel({ projectId, onClose }: Props) {
  const init = useBoard((s) => s.init)
  const save = useBoard((s) => s.save)
  const shapes = useBoard((s) => s.shapes)
  const viewport = useBoard((s) => s.viewport)
  const [activeTool, setActiveTool] = useState<Tool>('select')
  const [shapeSubType, setShapeSubType] = useState<ShapeSubType>('rect')

  // ── Init on mount
  useEffect(() => { init(projectId) }, [projectId, init])

  // ── Auto-save (debounced 1s)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => save(), 1000)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
  }, [shapes, viewport, save])

  // ── Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.code === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="whiteboard-panel" style={{ background: '#ffffff' }}>
      {/* Topbar */}
      <div className="whiteboard-panel__topbar">
        <button type="button" className="whiteboard-panel__close" onClick={onClose}>
          ← Zurück
        </button>
        <div className="whiteboard-panel__title">Board</div>
        <div className="whiteboard-panel__spacer" />
        <BoardToolbar />
      </div>

      {/* Canvas + floating bottom bar */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <BoardCanvas activeTool={activeTool} shapeSubType={shapeSubType} />
        <BoardBottomBar activeTool={activeTool} onToolChange={setActiveTool} shapeSubType={shapeSubType} onShapeSubTypeChange={setShapeSubType} />
      </div>
    </div>
  )
}
